// src/components/tiptap/extensions.ts
// Custom TipTap extensions for the Reporter component.

import { Extension } from "@tiptap/core";
import { Node as ProseMirrorNode } from "@tiptap/pm/model";
import { TextSelection } from "@tiptap/pm/state";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet, type EditorView } from "@tiptap/pm/view";
import { GPT_IMPRESSION_DISCLOSURE } from "../../utils/impressionDisclosure";
import { CHIP_INNER_CLASS } from "./marks";
import { markImpressionStale } from "@/components/tiptap/impressionFreshness";

// Insert an empty rad-added field at `paraInnerEnd` (the position just inside
// the close of an organ subsection's paragraph) and focus its inner span so
// the rad can type immediately. Shared by the "+ Add finding" widget and the
// chip right-click "Add finding" action so the two stay in lockstep.
export function insertRadFinding(view: EditorView, paraInnerEnd: number): void {
  const fieldType = view.state.schema.nodes.field;
  if (!fieldType) return;
  const tr = view.state.tr;
  // Space separator if the tail isn't already whitespace — keeps chip spacing
  // consistent.
  const charBefore = paraInnerEnd > 0 ? tr.doc.textBetween(paraInnerEnd - 1, paraInnerEnd) : "";
  const needsSpace = !!charBefore && charBefore !== " " && charBefore !== "\n";
  if (needsSpace) tr.insert(paraInnerEnd, view.state.schema.text(" "));
  const insertAt = needsSpace ? paraInnerEnd + 1 : paraInnerEnd;
  const newId = crypto.randomUUID();
  tr.insert(insertAt, fieldType.create({ id: newId, kind: "rad-added", text: "" }));
  view.dispatch(tr.scrollIntoView());
  requestAnimationFrame(() => {
    const inner = view.dom.querySelector<HTMLElement>(
      `.report-field[data-id="${CSS.escape(newId)}"] .${CHIP_INNER_CLASS}`,
    );
    inner?.focus();
  });
}

// --- findAllFields: collect every field node in the doc. Each entry
// corresponds to one Tab-stop / one report chip.
export interface FieldRange {
  /** PM position just before the node's open token. */
  start: number;
  /** PM position just after the node's close token. */
  end: number;
  seriesId: string | null;
  sliceIndex: number | null;
}

export function findAllFields(doc: ProseMirrorNode): FieldRange[] {
  const fields: FieldRange[] = [];
  doc.descendants((node, pos) => {
    if (node.type.name !== "field") return true;
    fields.push({
      start: pos,
      end: pos + node.nodeSize,
      seriesId: (node.attrs.seriesId as string | null) ?? null,
      sliceIndex: (node.attrs.sliceIndex as number | null) ?? null,
    });
    return false; // don't recurse into text children
  });
  return fields;
}

// --- resolveSectionKindAt: walk ancestors at `pos` and return the
// enclosing organSubsection's `kind` attr (e.g. "liver", "adrenal") or
// the literal "impression" if the position is inside an impressionSection.
// Used by reportFocusAtom writers as the anatomical anchor — replaces the
// LLM/keyword classifier that misrouted findings (#403).
export function resolveSectionKindAt(doc: ProseMirrorNode, pos: number): string | null {
  if (pos < 0 || pos > doc.content.size) return null;
  const $pos = doc.resolve(pos);
  for (let depth = $pos.depth; depth >= 0; depth--) {
    const node = $pos.node(depth);
    if (node.type.name === "organSubsection") {
      return (node.attrs.kind as string | null) ?? null;
    }
    if (node.type.name === "impressionSection") {
      return "impression";
    }
  }
  return null;
}

// --- findFieldNodeById: locate a field node by attrs.id. Returns the
// node's start position (PM position just before its open token) and the
// node itself. Used by replaceChipText (atomic update) and by ghost-text
// finalize to validate that the snapshot chipId still exists.
export function findFieldNodeById(
  doc: ProseMirrorNode,
  id: string,
): { node: ProseMirrorNode; pos: number } | null {
  let hit: { node: ProseMirrorNode; pos: number } | null = null;
  doc.descendants((node, pos) => {
    if (hit) return false;
    if (node.type.name !== "field") return true;
    if ((node.attrs.id as string | null) === id) {
      hit = { node, pos };
      return false;
    }
    return false;
  });
  return hit;
}

// --- paragraphToReportText: serialize one paragraph to its report text.
// Field nodes are atomic; their text lives on `attrs.text`. Two adjacent
// fields get a single space separator. hardBreak → "\n".
export function paragraphToReportText(paragraph: ProseMirrorNode): string {
  let line = "";
  let prevWasField = false;
  paragraph.forEach((child) => {
    if (child.type.name === "field") {
      if (prevWasField) line += " ";
      line += (child.attrs.text as string | null) ?? "";
      prevWasField = true;
      return;
    }
    if (child.type.name === "hardBreak") {
      line += "\n";
      prevWasField = false;
      return;
    }
    if (child.isText) {
      line += child.text ?? "";
      prevWasField = false;
      return;
    }
    prevWasField = false;
  });
  return line;
}

// --- FieldNavigation Extension: Tab/Shift-Tab between fields.
// Delegates to FieldNode's nextField / prevField commands, which read the
// current chip from focusedReportChipIdAtom (DOM caret inside a chip) or
// from PM selection (caret in prose) and fire the viewer slice jump via
// `editor.storage.field.onJump`.
export const FieldNavigation = Extension.create({
  name: "fieldNavigation",

  addKeyboardShortcuts() {
    return {
      Tab: () => this.editor.commands.nextField(),
      "Shift-Tab": () => this.editor.commands.prevField(),
    };
  },
});

// ImpressionReorderExtension (the hover-revealed ▲▼ inline buttons) deleted —
// PrototypeReport now wires @tiptap/extension-drag-handle and scopes it to
// impressionItem via the onNodeChange visibility toggle.
//
// Impression auto-numbering is handled by pure CSS (counter-reset +
// counter-increment on p[data-is-impression-item="true"]). See
// ReporterStyles.css.

export const FINDING_DRAG_MIME = "application/x-a2z-report-finding";

export interface DraggedFinding {
  editorId: string;
  findingId: string;
}

function findDraggableFinding(doc: ProseMirrorNode, findingId: string) {
  const source = findFieldNodeById(doc, findingId);
  if (!source || source.node.attrs.kind === "template") return null;
  const sectionKind = resolveSectionKindAt(doc, source.pos);
  if (sectionKind === null || sectionKind === "impression") return null;
  const text = source.node.attrs.text;
  return typeof text === "string" && text.trim() ? { findingId, text: text.trim() } : null;
}

/** Resolve the current source before creating an editable, cited Impression item. */
function appendFindingToImpression(view: EditorView, findingId: string): boolean {
  const finding = findDraggableFinding(view.state.doc, findingId);
  const { schema } = view.state;
  const itemType = schema.nodes.impressionItem;
  const paragraphType = schema.nodes.paragraph;
  const fieldType = schema.nodes.field;
  if (!itemType || !paragraphType || !fieldType || !finding) return false;

  let insertAt: number | null = null;
  view.state.doc.descendants((node, pos) => {
    if (node.type.name === "impressionSection") {
      insertAt = pos + node.nodeSize - 1;
      return false;
    }
    return true;
  });
  if (insertAt === null) return false;

  const impressionId = crypto.randomUUID();
  const field = fieldType.create({
    id: impressionId,
    kind: "rad-added",
    text: finding.text,
  });
  const item = itemType.create(
    {
      id: impressionId,
      citedFindings: [finding.findingId],
      provenance: "user",
    },
    paragraphType.create(null, field),
  );
  view.dispatch(view.state.tr.insert(insertAt, item).scrollIntoView());
  return true;
}

const findingToImpressionDropPluginKey = new PluginKey("findingToImpressionDrop");

function readDraggedFindingId(event: DragEvent, editorId: string): string | null {
  let finding: Partial<DraggedFinding> | null;
  try {
    finding = JSON.parse(event.dataTransfer?.getData(FINDING_DRAG_MIME) ?? "");
  } catch {
    // Unrecognized transfer data is a rejected drop, never a document mutation.
    return null;
  }
  return finding?.editorId === editorId && typeof finding.findingId === "string"
    ? finding.findingId
    : null;
}

export const FindingToImpressionDropExtension = Extension.create({
  name: "findingToImpressionDrop",

  addProseMirrorPlugins() {
    const editor = this.editor;
    const isImpressionTarget = (event: DragEvent) =>
      event.target instanceof Element &&
      Boolean(event.target.closest('section[data-section-kind="impression"]'));
    const hasFinding = (event: DragEvent) =>
      Array.from(event.dataTransfer?.types ?? []).includes(FINDING_DRAG_MIME);

    return [
      new Plugin({
        key: findingToImpressionDropPluginKey,
        props: {
          handleDOMEvents: {
            dragover: (_view, rawEvent) => {
              const event = rawEvent as DragEvent;
              if (!editor.isEditable || !hasFinding(event) || !isImpressionTarget(event)) {
                return false;
              }
              event.preventDefault();
              if (event.dataTransfer) event.dataTransfer.dropEffect = "copy";
              return true;
            },
            drop: (view, rawEvent) => {
              const event = rawEvent as DragEvent;
              view.dom.classList.remove("finding-drag-active");
              if (!hasFinding(event)) return false;
              // Consume rejected finding drops too, including any plain-text/HTML
              // representation that ProseMirror would otherwise paste by default.
              event.preventDefault();
              if (!editor.isEditable || !isImpressionTarget(event)) return true;
              const findingId = readDraggedFindingId(event, editor.instanceId);
              if (findingId && appendFindingToImpression(view, findingId)) {
                view.focus();
              }
              return true;
            },
          },
        },
      }),
    ];
  },
});

// --- AddImpressionItemExtension: trailing "+ Add impression" affordance.
// The shared add-field zones can only insert a new field inside the current
// impressionItem's paragraph. Rads need to grow the impression list itself —
// this widget sits at the end of impressionSection and inserts a sibling
// impressionItem (one empty paragraph + one empty field, cursor inside).
const addImpressionItemPluginKey = new PluginKey("addImpressionItem");

export const AddImpressionItemExtension = Extension.create({
  name: "addImpressionItem",

  addProseMirrorPlugins() {
    const editor = this.editor;
    return [
      new Plugin({
        key: addImpressionItemPluginKey,
        props: {
          decorations(state) {
            if (!editor.isEditable) return DecorationSet.empty;
            const { doc, schema } = state;
            const impressionType = schema.nodes.impressionSection;
            const itemType = schema.nodes.impressionItem;
            const paraType = schema.nodes.paragraph;
            if (!impressionType || !itemType || !paraType) {
              return DecorationSet.empty;
            }

            let sectionEnd: number | null = null;
            doc.descendants((node, pos) => {
              if (node.type.name === "impressionSection") {
                sectionEnd = pos + node.nodeSize - 1;
                return false;
              }
              return true;
            });
            if (sectionEnd === null) return DecorationSet.empty;

            const widget = Decoration.widget(
              sectionEnd,
              () => {
                const btn = document.createElement("button");
                btn.type = "button";
                btn.className = "add-impression-item-btn";
                btn.contentEditable = "false";
                btn.tabIndex = -1;
                btn.setAttribute("aria-label", "Add impression item");
                btn.title = "Add impression item";
                btn.textContent = "＋ Add impression";
                btn.onmousedown = (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const view = editor.view;
                  // Atomic fields can't host an inner caret via PM. Insert an
                  // empty paragraph; the rad dictates / types inline prose
                  // and dictation appends field nodes to it.
                  const newPara = paraType.create(null);
                  const newItem = itemType.create(
                    {
                      id: crypto.randomUUID(),
                      citedFindings: [],
                      provenance: "user",
                    },
                    newPara,
                  );
                  const tr = view.state.tr;
                  let insertAt: number | null = null;
                  tr.doc.descendants((node, pos) => {
                    if (node.type.name === "impressionSection") {
                      insertAt = pos + node.nodeSize - 1;
                      return false;
                    }
                    return true;
                  });
                  if (insertAt === null) return;
                  tr.insert(insertAt, newItem);
                  // Cursor inside the new empty paragraph. Path:
                  //   insertAt → impressionItem open
                  //   +1       → paragraph open
                  //   +1       → paragraph content start
                  tr.setSelection(TextSelection.create(tr.doc, insertAt + 2));
                  view.dispatch(tr.scrollIntoView());
                  view.focus();
                };
                return btn;
              },
              { side: 1 },
            );
            return DecorationSet.create(doc, [widget]);
          },
        },
      }),
    ];
  },
});

// --- RegenerateImpressionExtension: a "↻" button at the IMPRESSION header.
// Regenerating the impression is contextual to the impression, so the control
// lives at the section header (absolute top-right via CSS) rather than in the
// global control bar. The click bridges to React through editor.storage —
// same pattern as FieldNode.onJump — because the actual work is an async LLM
// stream owned by PrototypeReport, not a pure editor command. The storage
// `streaming` drives the spinner; stale state lives on the saved Impression node.
const regenerateImpressionPluginKey = new PluginKey("regenerateImpression");

export interface RegenerateImpressionStorage {
  onRegenerate: (() => void) | null;
  streaming: boolean;
}

export const RegenerateImpressionExtension = Extension.create({
  name: "regenerateImpression",

  addStorage(): RegenerateImpressionStorage {
    return { onRegenerate: null, streaming: false };
  },

  addProseMirrorPlugins() {
    const editor = this.editor;
    return [
      new Plugin({
        key: regenerateImpressionPluginKey,
        appendTransaction: (_transactions, before, after) => markImpressionStale(before, after),
        props: {
          decorations(state) {
            const { doc } = state;
            let contentStart: number | null = null;
            let hasGptImpression = false;
            let stale = false;
            doc.descendants((node, pos) => {
              if (node.type.name === "impressionSection") {
                contentStart = pos + 1; // first position inside the section
                stale = node.attrs.findingsChanged === true;
                node.descendants((child) => {
                  if (
                    child.type.name === "impressionItem" &&
                    child.attrs.provenance === "ai-detected"
                  ) {
                    hasGptImpression = true;
                    return false;
                  }
                  return true;
                });
                return false;
              }
              return true;
            });
            if (contentStart === null) return DecorationSet.empty;
            const decorations: Decoration[] = [];
            if (stale) {
              decorations.push(
                Decoration.widget(contentStart, createImpressionStaleNotice, {
                  side: -3,
                  key: "impression-stale-notice",
                }),
              );
            }
            if (hasGptImpression) {
              decorations.push(
                Decoration.widget(
                  contentStart,
                  () => {
                    const note = document.createElement("div");
                    note.className = "gpt-impression-disclosure";
                    note.contentEditable = "false";
                    note.textContent = GPT_IMPRESSION_DISCLOSURE;
                    return note;
                  },
                  { side: -2, key: "gpt-impression-disclosure" },
                ),
              );
            }
            if (!editor.isEditable) return DecorationSet.create(doc, decorations);
            const storage = editor.storage.regenerateImpression as RegenerateImpressionStorage;
            decorations.push(
              Decoration.widget(
                contentStart,
                () => {
                  const btn = document.createElement("button");
                  btn.type = "button";
                  btn.className = `regen-impression-btn${stale ? " is-stale" : ""}`;
                  btn.contentEditable = "false";
                  btn.tabIndex = -1;
                  btn.disabled = storage.streaming;
                  btn.title = stale
                    ? "Findings changed - regenerate impression (Ctrl+G)"
                    : "Regenerate impression (Ctrl+G)";
                  btn.setAttribute("aria-label", "Regenerate impression");
                  const icon = document.createElement("span");
                  icon.className = `regen-impression-btn__icon${storage.streaming ? " is-spinning" : ""}`;
                  icon.textContent = "↻";
                  btn.appendChild(icon);
                  btn.onmousedown = (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!storage.streaming) storage.onRegenerate?.();
                  };
                  return btn;
                },
                // Key on the flags so PM rebuilds the widget DOM when they change.
                { side: -1, key: `regen-${storage.streaming}-${stale}` },
              ),
            );
            return DecorationSet.create(doc, decorations);
          },
        },
      }),
    ];
  },
});

function createImpressionStaleNotice() {
  const notice = document.createElement("div");
  notice.className = "impression-stale-notice";
  notice.contentEditable = "false";
  notice.setAttribute("role", "status");
  const icon = document.createElement("span");
  icon.setAttribute("aria-hidden", "true");
  icon.textContent = "⚠︎";
  const label = document.createElement("span");
  label.textContent = "Findings edited";
  notice.append(icon, label);
  return notice;
}

// Meta key for forcing the regenerate widget to recompute when the React-side
// streaming state changes (the storage mutation alone doesn't trigger
// a decoration recompute — a transaction does).
export const regenerateImpressionRefreshMeta = "regenerateImpressionRefresh";

// Tiptap 3 tightened `editor.storage` typing — declare this extension's
// storage shape so consumers read it without casts (merges with the
// `field` storage declared in ./marks.ts).
declare module "@tiptap/core" {
  interface Storage {
    regenerateImpression: RegenerateImpressionStorage;
  }
}

// --- AddOrganFindingExtension: trailing "+ Add finding" affordance per organ.
// Voice + AI-suggestion-accept handle the bulk of chip creation; this button
// is the keyboard escape hatch for the rare case where a rad needs to type a
// finding into a section that has no AI chip to anchor onto. One button per
// organSubsection, rendered after the section's content.
const addOrganFindingPluginKey = new PluginKey("addOrganFinding");

export const AddOrganFindingExtension = Extension.create({
  name: "addOrganFinding",

  addProseMirrorPlugins() {
    const editor = this.editor;
    return [
      new Plugin({
        key: addOrganFindingPluginKey,
        props: {
          decorations(state) {
            if (!editor.isEditable) return DecorationSet.empty;
            const { doc, schema } = state;
            const fieldType = schema.nodes.field;
            const subsectionType = schema.nodes.organSubsection;
            if (!fieldType || !subsectionType) return DecorationSet.empty;

            const decorations: Decoration[] = [];
            doc.descendants((node, pos) => {
              // Walk into findingsSection to reach the organSubsections.
              if (node.type.name === "findingsSection") return true;
              if (node.type.name !== "organSubsection") return false;

              // Place the button INSIDE the first paragraph (just before its
              // close token), so it renders inline at the end of the chip row
              // rather than on a separate line below. Hover-only opacity keeps
              // the chip text from jumping when the button reveals.
              let firstParaInner: number | null = null;
              node.forEach((child, childOffset) => {
                if (firstParaInner !== null || child.type.name !== "paragraph") return;
                const paraStart = pos + 1 + childOffset;
                firstParaInner = paraStart + child.nodeSize - 1;
              });
              if (firstParaInner === null) return false;
              const targetInside: number = firstParaInner;

              const widget = Decoration.widget(
                targetInside,
                () => {
                  const btn = document.createElement("button");
                  btn.type = "button";
                  btn.className = "add-organ-finding-btn";
                  btn.contentEditable = "false";
                  btn.tabIndex = -1;
                  btn.setAttribute("aria-label", "Add finding to this section");
                  btn.title = "Add finding";
                  btn.textContent = "＋";
                  btn.onmousedown = (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    insertRadFinding(editor.view, targetInside);
                  };
                  return btn;
                },
                { side: 1 },
              );
              decorations.push(widget);
              return false;
            });

            return DecorationSet.create(doc, decorations);
          },
        },
      }),
    ];
  },
});

// Classify a section paragraph for the section-remove confirm gate (owned
// by the OrganSubsection NodeView in ./nodes.ts):
//   - empty: no fields, no non-whitespace text — silent delete
//   - templateOnly: only untouched template chips ("Normal liver." default) —
//     silent delete; the rad never authored anything that would be lost
//   - otherwise (ai-finding chips, rad-added chips, edited template chips
//     promoted to rad-added by ChipView, raw prose) → window.confirm
//
// ChipView's handleInput promotes kind="template" → kind="rad-added" on the
// first real text edit, so an in-place rewrite of "Normal liver." surfaces
// here as kind="rad-added" and trips the confirm path. Untouched template
// chips keep kind="template" and delete silently.
export function paragraphIsEmptyOrTemplateOnly(para: ProseMirrorNode): {
  empty: boolean;
  templateOnly: boolean;
  nonTemplateChipCount: number;
} {
  let templateOnly = true;
  let empty = true;
  let nonTemplateChipCount = 0;
  para.forEach((child) => {
    if (child.type.name === "field") {
      empty = false;
      const kind = child.attrs.kind as string | null;
      if (kind !== "template") {
        templateOnly = false;
        nonTemplateChipCount += 1;
      }
    } else if (child.isText && (child.text ?? "").trim()) {
      empty = false;
      templateOnly = false;
    }
  });
  return { empty, templateOnly, nonTemplateChipCount };
}

// Confirm-dialog message for removing an organ subsection. Shared by the
// template-edit × button (OrganSubsection NodeView in ./nodes.ts) and the
// case-viewer right-click "Remove section" menu (PrototypeReport) so the
// wording — and the empty / template / finding classification it depends on
// — never drifts between the two call sites.
export function buildSectionRemoveConfirm(section: ProseMirrorNode): string {
  const para = section.firstChild;
  const info = para
    ? paragraphIsEmptyOrTemplateOnly(para)
    : { empty: true, templateOnly: false, nonTemplateChipCount: 0 };
  const detail =
    info.nonTemplateChipCount > 0
      ? ` ${info.nonTemplateChipCount} finding${info.nonTemplateChipCount === 1 ? "" : "s"} will be dropped.`
      : info.templateOnly
        ? " The default text will be removed."
        : "";
  return `Remove ${section.attrs.label}?${detail}`;
}

// Find the organSubsection with the given kind (kebab slug, unique per
// template). Used by the section right-click menu (rename / remove).
export function findOrganSubsectionByKind(
  doc: ProseMirrorNode,
  kind: string,
): { pos: number; node: ProseMirrorNode } | null {
  const matches: { pos: number; node: ProseMirrorNode }[] = [];
  doc.descendants((node, pos) => {
    if (matches.length) return false;
    if (node.type.name === "organSubsection" && node.attrs.kind === kind) {
      matches.push({ pos, node });
      return false;
    }
    return true;
  });
  return matches[0] ?? null;
}

// Rename an organ subsection's label in place. Edits THIS doc's node only —
// per-report in the case viewer, per-template in the template editor — never
// the global template. Returns true when applied.
export function renameOrganSubsectionLabel(
  view: EditorView,
  kind: string,
  newLabel: string,
): boolean {
  const found = findOrganSubsectionByKind(view.state.doc, kind);
  if (!found) return false;
  view.dispatch(
    view.state.tr.setNodeMarkup(found.pos, null, { ...found.node.attrs, label: newLabel }),
  );
  return true;
}

// --- DictationGhost Extension: inline preview of interim transcript at cursor.
const dictationGhostPluginKey = new PluginKey("dictationGhost");

export { dictationGhostPluginKey };

export const DictationGhostExtension = Extension.create({
  name: "dictationGhost",

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: dictationGhostPluginKey,
        state: {
          init: () => DecorationSet.empty,
          apply(tr, set) {
            const meta = tr.getMeta(dictationGhostPluginKey);
            if (meta !== undefined) {
              if (!meta) return DecorationSet.empty;
              const { pos, text } = meta as { pos: number; text: string };
              const clampedPos = Math.min(pos, tr.doc.content.size);
              const widget = Decoration.widget(
                clampedPos,
                () => {
                  const span = document.createElement("span");
                  span.className = "dictation-ghost";
                  span.textContent = text;
                  span.setAttribute("contenteditable", "false");
                  return span;
                },
                { side: 1 },
              );
              return DecorationSet.create(tr.doc, [widget]);
            }
            return set.map(tr.mapping, tr.doc);
          },
        },
        props: {
          decorations(state) {
            return dictationGhostPluginKey.getState(state);
          },
        },
      }),
    ];
  },
});

// --- ReportStructureGuard: filterTransaction-level protection against
// destructive operations that would remove structural nodes the rad
// can't legitimately want to delete (FINDINGS heading, IMPRESSION
// heading). Cmd+A + Backspace and similar "select all and nuke" paths
// would otherwise collapse the doc to an empty paragraph and lose
// every chip in one keystroke.
//
// Impression items, organSubsections, and field nodes are NOT protected
// from deletion — deleting any of them is a legitimate rad workflow.
// Organ subsection creation is also allowed: undo of a section-remove
// and Clear Draft both need to restore subsections. Two attr-level
// checks catch the paste-spawned cases the count check used to block:
//   - hasOrphanOrganSubsection rejects subsections with empty kind/label
//     (a PM split that copies the parent type but defaults the attrs).
//   - hasDuplicateOrganSubsectionKind rejects subsections whose kind
//     collides with one already in the doc — the failure mode an actual
//     paste of HTML carrying `data-organ-subsection` triggers, where PM
//     honors the attrs verbatim and a copy across two sections yields
//     two valid-attr LIVERs.
const reportStructureGuardKey = new PluginKey("reportStructureGuard");

function countStructuralNodes(doc: ProseMirrorNode): {
  findings: number;
  impression: number;
} {
  let findings = 0;
  let impression = 0;
  doc.descendants((node) => {
    if (node.type.name === "findingsSection") findings += 1;
    else if (node.type.name === "impressionSection") impression += 1;
  });
  return { findings, impression };
}

/** True if the doc contains any organSubsection without a valid `kind`
 * + `label`. PM can spawn rogue subsections when a paste of multi-line
 * text overflows a `content: "paragraph"` parent and PM splits the
 * grandparent to make room. Empty-attr subsections are invalid. */
function hasOrphanOrganSubsection(doc: ProseMirrorNode): boolean {
  let found = false;
  doc.descendants((node) => {
    if (found) return false;
    if (node.type.name !== "organSubsection") return true;
    const kind = node.attrs.kind as string | undefined;
    const label = node.attrs.label as string | undefined;
    if (!kind || !label) found = true;
    return false;
  });
  return found;
}

/** True if the doc contains two organSubsection nodes with the same
 * `kind`. Triggered by paste of HTML that carries
 * `data-organ-subsection` (e.g. a Cmd+C across two subsections of a
 * prior report): PM honors the attrs verbatim and the duplicate sails
 * past hasOrphanOrganSubsection. Undo restore and Clear Draft both
 * yield unique-kind sets so this check is a no-op for legitimate
 * creation paths. */
function hasDuplicateOrganSubsectionKind(doc: ProseMirrorNode): boolean {
  const seen = new Set<string>();
  let dup = false;
  doc.descendants((node) => {
    if (dup) return false;
    if (node.type.name !== "organSubsection") return true;
    const kind = (node.attrs.kind as string | undefined) ?? "";
    if (!kind) return false;
    if (seen.has(kind)) {
      dup = true;
      return false;
    }
    seen.add(kind);
    return false;
  });
  return dup;
}

export const ReportStructureGuard = Extension.create<{ isLocked: () => boolean }>({
  name: "reportStructureGuard",
  addOptions() {
    return { isLocked: () => false };
  },
  addProseMirrorPlugins() {
    const isLocked = this.options.isLocked;
    return [
      new Plugin({
        key: reportStructureGuardKey,
        filterTransaction(tr, state) {
          if (!tr.docChanged) return true;
          if (isLocked()) return false;
          const before = countStructuralNodes(state.doc);
          // Skip the guard until the doc has been initialized with the
          // template-driven build; the editor briefly mounts empty.
          if (before.findings === 0 && before.impression === 0) return true;
          const after = countStructuralNodes(tr.doc);
          if (after.findings < before.findings) return false;
          if (after.impression < before.impression) return false;
          if (hasOrphanOrganSubsection(tr.doc)) return false;
          if (hasDuplicateOrganSubsectionKind(tr.doc)) return false;
          return true;
        },
      }),
    ];
  },
});

// EmptyFieldGC deleted. Atomic chips can't decay into empty-text containers
// the way the old `content: "text*"` shape could. Empty atomic chips persist
// until explicitly removed (Backspace adjacent, card Remove, section delete).

// --- EmptyImpressionItemGC: garbage-collect empty impressionItem nodes
// when the cursor leaves them. Mirrors EmptyFieldGC at the impressionItem
// level: pressing Enter (or clicking "+ Add impression") creates a new
// item with an empty field; if the rad walks away without typing, the
// stale item would persist as a numbered "[ ]" placeholder.
//
// "Empty" = the item's single paragraph has no fields with content and
// no other text. The cursor-inside check uses the whole item's range so
// the rad keeps editing freely.
const emptyImpressionItemGCKey = new PluginKey("emptyImpressionItemGC");

export const EmptyImpressionItemGC = Extension.create({
  name: "emptyImpressionItemGC",
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: emptyImpressionItemGCKey,
        appendTransaction(_trs, _oldState, newState) {
          const sel = newState.selection;
          const ranges: { from: number; to: number }[] = [];
          newState.doc.descendants((node, pos) => {
            if (node.type.name === "impressionSection") return true;
            if (node.type.name !== "impressionItem") return false;
            const start = pos;
            const end = pos + node.nodeSize;
            const cursorInside = sel.from >= start && sel.to <= end;
            if (cursorInside) return false;
            // Sum up meaningful content across all descendants. A field
            // with non-empty content counts; raw text outside fields counts;
            // empty fields don't.
            let meaningful = 0;
            node.descendants((child) => {
              if (child.type.name === "field") {
                meaningful += ((child.attrs.text as string | null) ?? "").trim().length;
                return false;
              }
              if (child.isText) {
                meaningful += child.text?.trim().length ?? 0;
              }
              return true;
            });
            if (meaningful === 0) ranges.push({ from: start, to: end });
            return false;
          });
          if (ranges.length === 0) return null;
          const tr = newState.tr;
          ranges.sort((a, b) => b.from - a.from);
          for (const r of ranges) tr.delete(r.from, r.to);
          return tr.steps.length > 0 ? tr : null;
        },
      }),
    ];
  },
});

// --- ImpressionParagraphAttrs: preserve data-* attributes on paragraph nodes.
// StarterKit's Paragraph drops unknown HTML attributes on parse. Impression
// item rendering sets `data-is-impression-item="true"` on streamed <p> tags;
// without this extension those attributes are stripped before
// ImpressionReorderExtension and the CSS counter selector see them.
export const ImpressionParagraphAttrs = Extension.create({
  name: "impressionParagraphAttrs",
  addGlobalAttributes() {
    return [
      {
        types: ["paragraph"],
        attributes: {
          "data-is-impression-item": {
            default: null,
            parseHTML: (el) => el.getAttribute("data-is-impression-item"),
            renderHTML: (attrs) => {
              const v = attrs["data-is-impression-item"];
              return v ? { "data-is-impression-item": v } : {};
            },
          },
          "data-group-id": {
            default: null,
            parseHTML: (el) => el.getAttribute("data-group-id"),
            renderHTML: (attrs) => {
              const v = attrs["data-group-id"];
              return v ? { "data-group-id": v } : {};
            },
          },
          "data-id": {
            default: null,
            parseHTML: (el) => el.getAttribute("data-id"),
            renderHTML: (attrs) => {
              const v = attrs["data-id"];
              return v ? { "data-id": v } : {};
            },
          },
          "data-cited-findings": {
            default: null,
            parseHTML: (el) => el.getAttribute("data-cited-findings"),
            renderHTML: (attrs) => {
              const v = attrs["data-cited-findings"];
              return v ? { "data-cited-findings": v } : {};
            },
          },
          "data-provenance": {
            default: null,
            parseHTML: (el) => el.getAttribute("data-provenance"),
            renderHTML: (attrs) => {
              const v = attrs["data-provenance"];
              return v ? { "data-provenance": v } : {};
            },
          },
        },
      },
    ];
  },
});
